import { Controller } from "@hotwired/stimulus"
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from "chart.js"
import type { ChartConfiguration, TooltipItem } from "chart.js"
import { TimeSeriesGestures, TimeSeriesViewport, ViewportDirection } from "../charts/time_series_viewport"

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
)

const MAX_VISIBLE_POINTS = 320

export default class PerformanceChartController extends Controller {
  static targets = ["canvas", "earlier", "later", "reset", "status"]
  static values = { config: Object }

  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly earlierTarget: HTMLButtonElement
  declare readonly laterTarget: HTMLButtonElement
  declare readonly resetTarget: HTMLButtonElement
  declare readonly statusTarget: HTMLParagraphElement
  declare readonly configValue: ChartConfiguration

  private chart?: Chart
  private viewport?: TimeSeriesViewport<number>
  private gestures?: TimeSeriesGestures

  connect() {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const hasTradeAxis = this.configValue.data?.datasets?.some(
      (dataset) => "yAxisID" in dataset && dataset.yAxisID === "yTrades"
    )
    const pointCount = this.configValue.data?.labels?.length || 0
    const coverageEnd = Math.max(pointCount - 1, 0)
    const viewportStart = pointCount > MAX_VISIBLE_POINTS ? pointCount - MAX_VISIBLE_POINTS : 0
    this.viewport = new TimeSeriesViewport({
      points: Array.from({ length: pointCount }, (_, index) => index),
      position: (index) => index,
      viewport: { startsAt: viewportStart, endsAt: coverageEnd },
      coverage: { startsAt: 0, endsAt: coverageEnd },
      minimumSpan: Math.min(8, coverageEnd),
      onChange: () => this.updateViewport()
    })
    this.chart = new Chart(this.canvasTarget, {
      ...this.configValue,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: reduceMotion ? false : { duration: 220 },
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: { usePointStyle: true, boxWidth: 8, padding: 18 }
          },
          tooltip: {
            callbacks: {
              label: (context: TooltipItem<"line" | "bar">) =>
                context.dataset.yAxisID === "yTrades"
                  ? `${context.dataset.label}: ${Number(context.raw).toLocaleString()}`
                  : `${context.dataset.label}: ${Number(context.raw).toFixed(2)}%`
            }
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            grid: { color: "rgba(23,25,20,.08)" },
            ticks: { callback: (value) => `${value}%` }
          },
          yTrades: {
            display: hasTradeAxis,
            position: "right",
            beginAtZero: true,
            grid: { drawOnChartArea: false },
            ticks: { precision: 0 }
          }
        }
      }
    } as ChartConfiguration)
    this.gestures = new TimeSeriesGestures(this.canvasTarget, {
      onPan: (fraction) => this.viewport?.panByFraction(fraction),
      onZoom: (factor, anchor) => this.viewport?.zoomByFactor(factor, anchor),
      onSelect: (startsFraction, endsFraction) => this.zoomToSelection(startsFraction, endsFraction)
    })
    this.gestures.connect()
    this.updateViewport()
  }

  disconnect() {
    this.gestures?.disconnect()
    this.chart?.destroy()
  }

  earlier() {
    this.viewport?.page("earlier")
  }

  later() {
    this.viewport?.page("later")
  }

  reset() {
    this.viewport?.reset()
  }

  navigate(event: KeyboardEvent) {
    if (["+", "="].includes(event.key)) {
      event.preventDefault()
      this.viewport?.zoomByFactor(0.72)
      return
    }
    if (["-", "_"].includes(event.key)) {
      event.preventDefault()
      this.viewport?.zoomByFactor(1.38)
      return
    }
    if (event.key === "0") {
      event.preventDefault()
      this.viewport?.reset()
      return
    }
    if (!event.shiftKey || !event.key.match(/^Arrow(Left|Right)$/)) return

    event.preventDefault()
    this.viewport?.page(event.key === "ArrowLeft" ? "earlier" : "later")
  }

  private updateViewport() {
    if (!this.viewport) return
    const range = this.viewport.range
    const xScale = this.chart?.options.scales?.x
    if (xScale) {
      xScale.min = Math.floor(range.startsAt)
      xScale.max = Math.ceil(range.endsAt)
      this.chart?.update("none")
    }
    this.updateNavigation()
  }

  private updateNavigation() {
    if (!this.viewport) return
    this.earlierTarget.disabled = !this.viewport.canPan("earlier")
    this.laterTarget.disabled = !this.viewport.canPan("later")
    this.resetTarget.disabled = !this.viewport.canReset
    const range = this.viewport.range
    const count = this.configValue.data?.labels?.length || 0
    const shown = count === 0 ? "No points" : `Showing ${Math.floor(range.startsAt) + 1}–${Math.ceil(range.endsAt) + 1} of ${count}`
    this.statusTarget.textContent = `${shown} · wheel to zoom · drag to select`
  }

  private zoomToSelection(startsFraction: number, endsFraction: number) {
    if (!this.viewport) return
    const range = this.viewport.range
    const span = range.endsAt - range.startsAt
    this.viewport.zoomTo({
      startsAt: range.startsAt + span * startsFraction,
      endsAt: range.startsAt + span * endsFraction
    })
  }
}
